(() => {
  const SELECTOR = '[data-cartas-waitlist]';
  const DEFAULT_DDI = '55';
  const PHONE_MIN_DIGITS = 8;
  const PHONE_MAX_DIGITS = 15;

  function onlyDigits(value) {
    return String(value || '').replace(/\D+/g, '');
  }

  function openDialog(dialog) {
    if (!dialog) return;

    if (typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
    }
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (dialog.open) dialog.close();
  }

  function isWaitlistTrigger(trigger, sectionEl) {
    if (!trigger) return false;

    const href = trigger.getAttribute('href') || '';
    const triggerPath = sectionEl.dataset.triggerPath || '/experiencia/cartas/lista-de-espera';

    if (trigger.hasAttribute('data-cartas-waitlist-open')) return true;
    if (triggerPath && href.includes(triggerPath)) return true;

    return false;
  }

  function initModalTriggers(sectionEl, dialog) {
    const closeButton = dialog.querySelector('[data-cartas-waitlist-close]');

    if (closeButton) {
      closeButton.addEventListener('click', () => closeDialog(dialog));
    }

    dialog.addEventListener('click', (event) => {
      const rect = dialog.getBoundingClientRect();
      const clickedInside =
        rect.top <= event.clientY &&
        event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX &&
        event.clientX <= rect.left + rect.width;

      if (!clickedInside) {
        closeDialog(dialog);
      }
    });

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('a[href], button[data-cartas-waitlist-open]');
      if (!trigger) return;
      if (!isWaitlistTrigger(trigger, sectionEl)) return;

      event.preventDefault();
      openDialog(dialog);
    });
  }

  function setPhoneValidity(input, message = '') {
    if (!input) return;
    input.setCustomValidity(message);
  }

  function formatBrazilNational(digits) {
    const nationalDigits = String(digits || '').slice(0, 11);

    if (!nationalDigits) return '';

    const ddd = nationalDigits.slice(0, 2);
    const rest = nationalDigits.slice(2);

    if (nationalDigits.length <= 2) {
      return `(${ddd}`;
    }

    if (rest.length <= 4) {
      return `(${ddd}) ${rest}`;
    }

    if (rest.length <= 8) {
      return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
    }

    return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;
  }

  function formatBrazilWithDdi(fullDigits) {
    const digits = String(fullDigits || '').startsWith(DEFAULT_DDI)
      ? String(fullDigits || '').slice(DEFAULT_DDI.length)
      : String(fullDigits || '');

    const formattedNational = formatBrazilNational(digits);

    return formattedNational ? `+${DEFAULT_DDI} ${formattedNational}` : `+${DEFAULT_DDI}`;
  }

  function groupInternationalDigits(digits) {
    return String(digits || '')
      .slice(0, PHONE_MAX_DIGITS)
      .replace(/(\d{1,3})(?=\d)/g, '$1 ')
      .trim();
  }

  function formatInternationalWithDdi(digits) {
    const groupedDigits = groupInternationalDigits(digits);
    return groupedDigits ? `+${groupedDigits}` : '+';
  }

  function formatPhoneInput(value) {
    const raw = String(value || '').trim();
    const hasExplicitDdi = raw.startsWith('+');
    const digits = onlyDigits(raw);

    if (!digits) {
      return hasExplicitDdi ? '+' : '';
    }

    // Se o usuário digitar +55..., aplica a máscara brasileira completa com DDI.
    if (hasExplicitDdi && digits.startsWith(DEFAULT_DDI)) {
      return formatBrazilWithDdi(digits);
    }

    // Se o usuário digitar +DDI diferente de +55, mantém padrão internacional genérico.
    if (hasExplicitDdi) {
      return formatInternationalWithDdi(digits);
    }

    // Se o usuário digitar 55 + DDD + número sem o sinal de +, trata como Brasil com DDI.
    if (digits.startsWith(DEFAULT_DDI) && digits.length > 11) {
      return formatBrazilWithDdi(digits);
    }

    // Sem +DDI, assume Brasil e adiciona +55 visualmente.
    return formatBrazilWithDdi(`${DEFAULT_DDI}${digits}`);
  }

  function normalizePhoneValue(value) {
    const raw = String(value || '').trim();
    const hasExplicitDdi = raw.startsWith('+');
    const digits = onlyDigits(raw).slice(0, PHONE_MAX_DIGITS);

    if (!digits) return '';

    if (hasExplicitDdi) {
      return `+${digits}`;
    }

    if (digits.startsWith(DEFAULT_DDI) && digits.length > 11) {
      return `+${digits}`;
    }

    return `+${DEFAULT_DDI}${digits.slice(0, 11)}`;
  }

  function isValidPhoneValue(value) {
    const normalized = normalizePhoneValue(value);
    const digits = onlyDigits(normalized);

    if (!digits) return false;

    if (digits.startsWith(DEFAULT_DDI)) {
      const nationalDigits = digits.slice(DEFAULT_DDI.length);
      return nationalDigits.length === 10 || nationalDigits.length === 11;
    }

    return digits.length >= PHONE_MIN_DIGITS && digits.length <= PHONE_MAX_DIGITS;
  }

  function syncPhoneValue(input, hiddenInput) {
    if (!input || !hiddenInput) return;

    const value = input.value.trim();

    if (!value) {
      hiddenInput.value = '';
      setPhoneValidity(input);
      return;
    }

    hiddenInput.value = normalizePhoneValue(value);
  }

  function validateAndSyncPhone(input, hiddenInput) {
    if (!input || !hiddenInput) return true;

    const value = input.value.trim();

    if (!value) {
      hiddenInput.value = '';
      setPhoneValidity(input);
      return !input.required;
    }

    if (!isValidPhoneValue(value)) {
      hiddenInput.value = '';
      setPhoneValidity(input, 'Informe um WhatsApp válido com DDI. Ex.: +55 (11) 99999-9999 ou +1 202 555 0199.');
      input.reportValidity();
      return false;
    }

    setPhoneValidity(input);
    hiddenInput.value = normalizePhoneValue(value);
    return true;
  }

  function initWhatsappField(form) {
    const input = form.querySelector('[data-cartas-waitlist-whatsapp]');
    const hiddenInput = form.querySelector('[data-cartas-waitlist-whatsapp-full]');

    if (!input || !hiddenInput || input.dataset.ready === 'true') {
      return null;
    }

    input.dataset.ready = 'true';

    input.addEventListener('input', () => {
      setPhoneValidity(input);
      input.value = formatPhoneInput(input.value);
      syncPhoneValue(input, hiddenInput);
    });

    input.addEventListener('blur', () => {
      input.value = formatPhoneInput(input.value);
      validateAndSyncPhone(input, hiddenInput);
    });

    syncPhoneValue(input, hiddenInput);

    return {
      input,
      hiddenInput
    };
  }

  function initForm(form) {
    const phoneField = initWhatsappField(form);

    form.addEventListener('submit', (event) => {
      if (!form.reportValidity()) {
        return;
      }

      const phoneIsValid = phoneField
        ? validateAndSyncPhone(phoneField.input, phoneField.hiddenInput)
        : true;

      if (!phoneIsValid) {
        event.preventDefault();
      }
    });
  }

  function initSection(sectionEl) {
    const dialog = sectionEl.querySelector('[data-cartas-waitlist-dialog]');
    const form = sectionEl.querySelector('.starina-cartas-modal__form');

    if (!dialog || !form || form.dataset.waitlistReady === 'true') return;
    form.dataset.waitlistReady = 'true';

    initModalTriggers(sectionEl, dialog);
    initForm(form);

    const result = form.querySelector('[data-cartas-waitlist-result]');

    if (result) {
      openDialog(dialog);
    }
  }

  function init() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
